import React, { useRef, useState } from 'react';
import { ProgressBar } from '../primitives/ProgressBar';
import { inkPanel } from '../theme/helpers';
import { useHudMood } from '../contexts/HudMoodContext';
import { COLORS, SPACE, FONT_SIZE, FONT_WEIGHT, RADII, INK } from '../theme';
import { QUEST_UI_CONFIG, QUEST_TYPE_ICON } from '../../../config/ui/quest.config';
import type { Quest, Helix2000Character } from '../../../shared/CharacterTypes';
import { questEndNpcDisplay } from '../../../shared/quests/questEndNpcDisplay';
import { windowBridge } from '../framework/windowBridge';
import { focusJournalQuest, questJournalOwner, useQuestJournalFocus } from '../Windows/Quest/questJournalFocus';
import { journalEntries, journalObjectives } from '../Windows/Quest/questJournalModel';
import { QUEST_TRACKER_GUIDANCE_ID, QuestMapButton, QuestTrackerGuidancePanel, questHasGuidance } from './QuestTrackerGuidance';
import type { HudConcreteStyle } from './useHudTomeVibe';
import { HudBranch, HudSprigRule, HudGlyph } from './tome/hudOrnaments';

export interface QuestTrackerProps {
  player?: Helix2000Character | null;
  onOpenQuest?: (questId: string) => void;
  hudStyle?: HudConcreteStyle;
}

/**
 * QuestTracker — always-visible objective list docked top-right (WoW-style).
 *
 * The Quest Log window (windowBridge.toggle('quests')) is the full journal;
 * this is the at-a-glance HUD companion that was previously missing entirely —
 * players had no on-screen sense of their active objectives without opening a
 * window. Reads the same live `player.quests` the log does, renders nothing when
 * there are no active quests (zero visual cost in empty zones), and caps the
 * list at config.maxTracked with a "+N more" overflow row.
 *
 * Pure presentation, theme-tokened, config-driven (anchor/width/colors in
 * quest.config.ts) — takes the live player as a prop (matching the other docked
 * HUD frames), so no GameContext coupling in the HUD render path. jsdom-safe.
 */
export const QuestTracker: React.FC<QuestTrackerProps> = ({ player, onOpenQuest, hudStyle }) => {
  const [hovered, setHovered] = useState(false);
  const [closed, setClosed] = useState(false);
  const [mapQuestId, setMapQuestId] = useState<string | null>(null);
  const [mapAnchor, setMapAnchor] = useState<{ top: number; right: number } | null>(null);
  const trackerRef = useRef<HTMLDivElement>(null);
  const isTome = Boolean(hudStyle);
  // Closing "where to go" from the keyboard (pin, close button or Esc) hands focus back to the pin
  // when it was in the panel or on the pin; a mouse or touch close leaves the keys to the game.
  const closeMap = (questId: string, byPointer = false) => {
    const active = document.activeElement;
    const pin = trackerRef.current?.querySelector<HTMLElement>(`[data-quest-map-button="${questId}"]`);
    const hadFocus = !byPointer && active instanceof Node
      && (active === pin || document.getElementById(QUEST_TRACKER_GUIDANCE_ID)?.contains(active));
    setMapQuestId(null);
    if (hadFocus) pin?.focus();
  };
  // The map docks beside the tracker where the HUD layout actually put it
  // (layout offsets in the shared containing block, so HUD scaling can't skew it).
  const toggleMap = (questId: string, byPointer: boolean) => {
    if (mapQuestId === questId) { closeMap(questId, byPointer); return; }
    const el = trackerRef.current;
    const parent = el?.offsetParent as HTMLElement | null | undefined;
    setMapAnchor(el && parent && el.offsetWidth > 0
      ? { top: el.offsetTop, right: parent.clientWidth - el.offsetLeft + SPACE.sm } : null);
    setMapQuestId(questId);
  };
  const { moodTint } = useHudMood(); // §6 — panel base tint only
  const entries = journalEntries(player?.quests ?? { active: [], completed: [] });
  const active = entries.active.map(entry => entry.live!);
  const owner = questJournalOwner(player);
  const focus = useQuestJournalFocus(owner, active.map(quest => quest.id), [...entries.active, ...entries.completed].map(quest => quest.id));
  const openDetails = (questId: string) => {
    if (onOpenQuest) { onOpenQuest(questId); return; }
    if (focusJournalQuest(owner, questId)) windowBridge.open('quests');
  };

  const { tracker, colors } = QUEST_UI_CONFIG;

  if (active.length === 0) return null;

  // Dismissed by the player → collapse to a small reopen pill (mirrors the
  // minimap's hide/show). Stays anchored in the same spot so it's findable.
  if (closed) {
    return (
      <button
        type="button"
        id="quest-tracker-reopen"
        onClick={() => setClosed(false)}
        title="Show quest tracker"
        data-hud-style={hudStyle}
        style={{
          position: 'absolute',
          top: tracker.anchor.top,
          right: tracker.anchor.right,
          zIndex: tracker.zIndex,
          pointerEvents: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: SPACE.xs,
          padding: `${SPACE.xs}px ${SPACE.sm}px`,
          fontSize: FONT_SIZE.xs,
          fontWeight: FONT_WEIGHT.semibold,
          color: isTome ? 'var(--tome-ink, #2a2015)' : COLORS.textSecondary,
          cursor: 'pointer',
          background: isTome ? 'var(--tome-paper-1, #f4eee2)' : undefined,
          border: isTome ? '1px solid var(--tome-rule-soft, rgba(60,35,10,0.3))' : undefined,
          borderRadius: isTome ? 4 : undefined,
          boxShadow: isTome ? '0 4px 12px rgba(0,0,0,0.3)' : undefined,
          ...(!isTome ? inkPanel(moodTint) : {}),
        }}
      >
        📜 Quests ({active.length})
      </button>
    );
  }

  const focusedQuest = active.find(quest => quest.id === focus.trackerId);
  const ordered = focusedQuest ? [focusedQuest, ...active.filter(quest => quest.id !== focusedQuest.id)] : active;
  const shown = ordered.slice(0, tracker.maxTracked);
  const overflow = active.length - shown.length;
  const mapQuest = mapQuestId && shown.some(quest => quest.id === mapQuestId) ? mapQuestId : null;

  return (<>
    <div
      id="quest-tracker" ref={trackerRef}
      role="region" aria-label="Quest tracker"
      data-hud-style={hudStyle}
      className={isTome ? 'hudt-tracker' : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocusCapture={() => setHovered(true)}
      onBlurCapture={event => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setHovered(false); }}
      style={{
        position: 'absolute',
        top: tracker.anchor.top,
        right: tracker.anchor.right,
        width: tracker.width,
        maxHeight: `calc(100dvh - ${tracker.anchor.top + 12}px)`, overflowY: 'auto',
        zIndex: tracker.zIndex,
        pointerEvents: 'auto',
        opacity: hovered ? 1 : tracker.idleOpacity,
        transition: 'opacity 120ms ease',
        ...(!isTome ? { ...inkPanel(moodTint), padding: SPACE.md } : {}),
      }}
    >
      {isTome && <span className="hudt-ground" aria-hidden="true" />}
      {isTome && <HudBranch />}
      {isTome ? (
        <header className="hudt-tracker-head">
          <button
            type="button"
            className="hudt-tracker-title"
            aria-label="Open quest journal"
            onClick={() => windowBridge.open('quests')}
          >
            Quests
          </button>
          <button
            type="button"
            className="hudt-close"
            aria-label="Hide quest tracker"
            title="Hide quest tracker"
            onClick={() => setClosed(true)}
          >
            <HudGlyph kind="close" />
          </button>
        </header>
      ) : (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: SPACE.sm,
            position: 'sticky', top: -SPACE.md, background: INK.base, zIndex: 1,
          }}
        >
          <button
            type="button" aria-label="Open quest journal" onClick={() => windowBridge.open('quests')}
            style={{
              fontSize: FONT_SIZE.sm,
              fontWeight: FONT_WEIGHT.semibold,
              letterSpacing: 0.5,
              textTransform: 'uppercase',
              color: COLORS.textSecondary,
              border: 0, padding: 0, background: 'transparent', cursor: 'pointer', textAlign: 'left',
            }}
          >
            Quests
          </button>
          <button
            type="button"
            aria-label="Hide quest tracker"
            title="Hide quest tracker"
            onClick={() => setClosed(true)}
            style={{
              border: 'none',
              background: 'transparent',
              color: COLORS.textMuted,
              cursor: 'pointer',
              fontSize: FONT_SIZE.sm,
              lineHeight: 1,
              padding: 5, width: 28, height: 28,
            }}
          >
            ✕
          </button>
        </div>
      )}

      {shown.map((quest: Quest, i: number) => {
        const done = quest.completed;
        const endNpcName = quest.endNpcName ?? questEndNpcDisplay(quest.id).name;
        // Render authored per-objective rows when present; otherwise derive one
        // row from the flat quest type/targetId for back-compat.
        const objectives = journalObjectives(quest);

        if (isTome) {
          return (
            <React.Fragment key={quest.id}>
              {i > 0 && <HudSprigRule />}
              <article className="hudt-quest" data-ready={done || undefined}>
                <div className="hudt-quest-row">
                  <button
                    type="button"
                    className="hudt-quest-name"
                    aria-label={`Open ${quest.name} in journal`}
                    onClick={() => openDetails(quest.id)}
                  >
                    <HudGlyph kind={done ? 'ready' : 'quest'} />
                    <span>{quest.name}</span>
                  </button>
                  {questHasGuidance(quest.id) && (
                    <QuestMapButton
                      questId={quest.id}
                      questName={quest.name}
                      open={mapQuest === quest.id}
                      onToggle={byPointer => toggleMap(quest.id, byPointer)}
                    />
                  )}
                </div>

                <div
                  className="hudt-quest-npc"
                  data-testid={`quest-tracker-end-npc-${quest.id}`}
                >
                  Turn in to: {endNpcName}
                </div>

                {objectives.map((obj, objIdx) => {
                  const frac = obj.knownRequirement && obj.knownCurrent && obj.required > 0
                    ? Math.max(0, Math.min(1, obj.current / obj.required))
                    : 0;
                  return (
                    <div key={objIdx} className="hudt-obj" data-met={obj.met || undefined}>
                      <div className="hudt-obj-row">
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {obj.label}
                        </span>
                        <span className="hudt-obj-num">{obj.progress}</span>
                      </div>
                      {obj.knownRequirement && obj.knownCurrent && (
                        <span className="hudt-bar" data-kind={(done || obj.met) ? 'quest-met' : 'quest'}>
                          <span
                            className="hudt-bar-fill"
                            style={{ transform: `scaleX(${frac})` }}
                          />
                        </span>
                      )}
                      {obj.description && (
                        <div className="hudt-quest-desc">
                          {obj.description}
                        </div>
                      )}
                    </div>
                  );
                })}
                {done && (
                  <div style={{ color: '#a8efb6', fontSize: FONT_SIZE.xs, marginTop: SPACE.xs }}>
                    📬 Ready to turn in
                  </div>
                )}
              </article>
            </React.Fragment>
          );
        }

        return (
          <div key={quest.id} style={{ marginBottom: SPACE.md }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.xs }}>
              <button type="button" aria-label={`Open ${quest.name} in journal`} onClick={() => openDetails(quest.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: SPACE.xs,
                  fontSize: FONT_SIZE.md,
                  fontWeight: FONT_WEIGHT.medium,
                  color: done ? colors.complete : COLORS.textPrimary,
                  border: 0, background: 'transparent', padding: 0, flex: 1, minWidth: 0, textAlign: 'left', cursor: 'pointer',
                }}
              >
                <span aria-hidden>{QUEST_TYPE_ICON[quest.type] ?? '❔'}</span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {quest.name}
                </span>
              </button>
              {questHasGuidance(quest.id) && <QuestMapButton questId={quest.id} questName={quest.name} open={mapQuest === quest.id}
                onToggle={byPointer => toggleMap(quest.id, byPointer)} />}
            </div>

            <div
              data-testid={`quest-tracker-end-npc-${quest.id}`}
              style={{
                color: done ? colors.complete : COLORS.textSecondary,
                fontSize: FONT_SIZE.xs,
                marginTop: SPACE.xxs,
              }}
            >
              Turn in to: {endNpcName}
            </div>

            {objectives.map((obj, i) => {
              return (
                <div key={i} style={{ marginTop: i === 0 ? SPACE.xxs : SPACE.xs }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: FONT_SIZE.xs,
                      color: colors.objective,
                      margin: `${SPACE.xxs}px 0`,
                    }}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {obj.label}
                    </span>
                    <span>{obj.progress}</span>
                  </div>
                  {obj.knownRequirement && obj.knownCurrent && <ProgressBar
                    value={obj.current}
                    max={obj.required}
                    showPercentage={false}
                    height={5}
                    customColor={(done || obj.met) ? colors.complete : colors.progress}
                  />}
                  {obj.description && (
                    <div style={{ color: colors.description, fontSize: FONT_SIZE.xs, marginTop: SPACE.xxs }}>
                      {obj.description}
                    </div>
                  )}
                </div>
              );
            })}
            {done && (
              <div style={{ color: colors.complete, fontSize: FONT_SIZE.xs, marginTop: SPACE.xs }}>
                📬 Ready to turn in
              </div>
            )}
          </div>
        );
      })}

      {overflow > 0 && (
        <button
          type="button"
          className={isTome ? 'hudt-more' : undefined}
          onClick={() => windowBridge.open('quests')}
          aria-label={`Open journal with ${overflow} more active quests`}
          style={!isTome ? {
            fontSize: FONT_SIZE.xs,
            color: COLORS.textSecondary,
            textAlign: 'center',
            marginTop: SPACE.xs,
            paddingTop: SPACE.xs,
            borderTop: `1px solid ${COLORS.borderSubtle}`,
            borderRadius: RADII.sm,
            width: '100%', border: 0, background: 'transparent', cursor: 'pointer',
          } : undefined}
        >
          +{overflow} more · Open journal
        </button>
      )}
    </div>
    {mapQuest && <QuestTrackerGuidancePanel key={mapQuest} questId={mapQuest} questLog={player?.quests} playerClass={player?.class}
      anchor={mapAnchor ?? { top: tracker.anchor.top, right: tracker.anchor.right + tracker.width + SPACE.sm }} zIndex={tracker.zIndex + 1}
      onClose={byPointer => closeMap(mapQuest, byPointer)} />}
  </>);
};
